import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';

// Function to trigger file download
const downloadFile = (blob, fileName) => {
  // Detection for iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  
  const url = window.URL.createObjectURL(blob);
  
  if (isIOS) {
    // iOS often blocks programmatic clicks on invisible links or requires a direct window.open
    // For iOS, opening in a new tab is the most reliable way to trigger the "Open In" or download prompt
    window.location.assign(url);
  } else {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  }
};

/**
 * Generates a PDF from transaction data.
 * @param {Array<Object>} data The transaction data to export.
 * @param {string} title The title of the document.
 * @param {Array<string>} headers The columns for the table.
 * @param {Function} rowMapper Function to map data objects to table rows.
 */
export const exportToPDF = (data, title = 'Transaction History', headers, rowMapper, userName = 'Valued User') => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const now = new Date();
  const dateStr = now.toLocaleDateString();
  const timeStr = now.toLocaleTimeString();

  // Header Branding
  try {
    // Logo (Safzi-Logo.png is in public folder)
    doc.addImage('/Safzi-Logo.png', 'PNG', 14, 10, 10, 10);
  } catch {
    // Logo omitted if not found
  }

  doc.setFontSize(22);
  doc.setTextColor(47, 133, 90); // Soft Green
  doc.setFont('helvetica', 'bold');
  doc.text('Safzi', 26, 18);

  doc.setFontSize(14);
  doc.setTextColor(31, 41, 51); // Charcoal
  doc.text(title, 14, 28);

  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128); // Muted Gray
  doc.setFont('helvetica', 'normal');
  doc.text(`Transaction Statement for ${userName}`, 14, 34);
  doc.text(`Generated on ${dateStr} at ${timeStr}`, 14, 39);

  // Divider Line
  doc.setDrawColor(229, 231, 235);
  doc.line(14, 42, pageWidth - 14, 42);

  // 2. Table
  const tableColumn = headers || ["Date", "Order ID", "Type", "Details", "Amount", "Status"];
  const tableRows = [];

  data.forEach(tx => {
    let txData = rowMapper ? rowMapper(tx) : [
      tx.date || tx.time || '',
      tx.id,
      tx.type,
      tx.agent || tx.client || '',
      `$${Number(tx.amount).toFixed(2)}`,
      tx.status
    ];
    
    // Safety check: Replace ₦ with NGN for standard PDF fonts
    txData = txData.map(val => typeof val === 'string' ? val.replace('₦', 'NGN ') : val);
    tableRows.push(txData);
  });

  autoTable(doc, {
    head: [tableColumn],
    body: tableRows,
    startY: 46,
    theme: 'striped',
    headStyles: { fillColor: [47, 133, 90], fontStyle: 'bold' },
    styles: { font: 'helvetica', fontSize: 9 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
  });

  // 3. Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.text(
      'This is an official transaction statement generated by Safzi. For support, contact support@safzi.com',
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  const blob = doc.output('blob');
  downloadFile(blob, `safzi_statement_${dateStr.replace(/\//g, '-')}.pdf`);
};

/**
 * Generates a CSV from transaction data.
 * @param {Array<Object>} data The transaction data to export.
 */
export const exportToCSV = (data) => {
  const csvData = Papa.unparse(data);
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  downloadFile(blob, 'safzi_transactions.csv');
};
